<template>
	<div v-if="pendingImage" class="chat-window__attachment">
		<button
			type="button"
			class="chat-window__attachment-preview"
			:aria-label="$t('chat.photo_open')"
			@click="$emit('open-photo', pendingImage.dataUrl)"
		>
			<img :src="pendingImage.dataUrl" alt="" />
		</button>
		<button
			type="button"
			class="chat-window__attachment-remove"
			:disabled="sending"
			:aria-label="$t('chat.photo_remove')"
			@click="$emit('remove-image')"
		>
			<chat-icon name="close" :size="12" />
		</button>
	</div>

	<form class="chat-window__composer" @submit.prevent="$emit('send')">
		<input ref="file" type="file" accept="image/*" class="hidden" @change="onFilePicked" />
		<button
			type="button"
			class="chat-window__attach"
			:disabled="!canCompose || sending || preparingPhoto"
			:aria-label="$t('chat.attach_photo')"
			:title="$t('chat.attach_photo')"
			@click="pickPhoto"
		>
			<app-icon v-if="preparingPhoto" icon="spin-loading" />
			<chat-icon v-else name="image" :size="22" />
		</button>
		<textarea
			ref="input"
			:value="draft"
			class="chat-window__input"
			rows="1"
			:maxlength="maxLength"
			:placeholder="$t('chat.placeholder')"
			:aria-label="$t('chat.placeholder')"
			:disabled="!canCompose"
			@input="onInput"
			@keydown.enter="onEnter"
			@paste="onPaste"
		/>
		<button
			type="submit"
			class="chat-window__send"
			:disabled="sendDisabled"
			:aria-label="$t('chat.send')"
			:title="$t('chat.send')"
		>
			<app-icon v-if="sending" icon="spin-loading" />
			<chat-icon v-else name="send" :size="18" />
		</button>
	</form>
</template>

<script>
	import ChatIcon from './ChatIcon.vue';

	const MAX_INPUT_HEIGHT_PX = 120;

	export default {
		name: 'ChatComposer',
		components: { ChatIcon },
		props: {
			draft: { type: String, required: true },
			pendingImage: { type: Object, default: null },
			canCompose: { type: Boolean, required: true },
			sending: { type: Boolean, required: true },
			preparingPhoto: { type: Boolean, required: true },
			sendDisabled: { type: Boolean, required: true },
			maxLength: { type: Number, required: true },
		},
		emits: ['update:draft', 'input', 'send', 'attach', 'remove-image', 'open-photo'],
		methods: {
			onInput(event) {
				this.$emit('update:draft', event.target.value);
				this.autosize();
				this.$emit('input');
			},
			onEnter(event) {
				if (event.shiftKey || event.isComposing) {
					return;
				}
				event.preventDefault();
				this.$emit('send');
			},
			pickPhoto() {
				this.$refs.file.value = '';
				this.$refs.file.click();
			},
			onFilePicked(event) {
				const [file] = event.target.files || [];
				if (file) {
					this.$emit('attach', file);
				}
			},
			onPaste(event) {
				const items = event.clipboardData ? Array.from(event.clipboardData.items) : [];
				const item = items.find((entry) => entry.kind === 'file' && entry.type.startsWith('image/'));
				const file = item ? item.getAsFile() : null;
				if (file) {
					event.preventDefault();
					this.$emit('attach', file);
				}
			},
			autosize() {
				const input = this.$refs.input;
				if (input) {
					input.style.height = 'auto';
					input.style.height = `${Math.min(input.scrollHeight, MAX_INPUT_HEIGHT_PX)}px`;
				}
			},
			focus() {
				const input = this.$refs.input;
				if (input && !input.disabled) {
					input.focus();
				}
			},
		},
	};
</script>

<style lang="scss" scoped>
	$chat-green: #08cc04;
	$chat-green-dark: #037800;
	$ink: #1a202c;
	$muted: #718096;
	$line: #e2e8f0;
	$surface: #f7fafc;

	.chat-window__attachment {
		position: relative;
		flex-shrink: 0;
		padding: 10px 12px 0;
		background: var(--white);
		border-top: 1px solid $line;
	}

	.chat-window__attachment-preview {
		display: block;
		width: 64px;
		height: 64px;
		padding: 0;
		overflow: hidden;
		background: $line;
		border: 1px solid $line;
		border-radius: 10px;
		cursor: zoom-in;

		img {
			width: 100%;
			height: 100%;
			object-fit: cover;
		}
	}

	.chat-window__attachment-remove {
		position: absolute;
		top: 4px;
		left: 68px;
		display: flex;
		align-items: center;
		justify-content: center;
		width: 20px;
		height: 20px;
		color: var(--white);
		background: $ink;
		border: 0;
		border-radius: 9999px;
		cursor: pointer;
	}

	.chat-window__composer {
		display: flex;
		flex-shrink: 0;
		align-items: flex-end;
		gap: 6px;
		padding: 10px 10px 12px;
		background: var(--white);
		border-top: 1px solid $line;
	}

	.chat-window__attach,
	.chat-window__send {
		display: flex;
		flex-shrink: 0;
		align-items: center;
		justify-content: center;
		width: 36px;
		height: 36px;
		border: 0;
		border-radius: 9999px;
		cursor: pointer;
	}

	.chat-window__attach {
		color: $muted;
		background: $surface;

		&:hover:not(:disabled) {
			color: $ink;
			background: #edf2f7;
		}
	}

	.chat-window__send {
		color: var(--white);
		background: $chat-green;

		&:hover:not(:disabled) {
			background: $chat-green-dark;
		}
	}

	.chat-window__input {
		flex: 1;
		min-width: 0;
		height: 36px;
		max-height: 120px;
		padding: 8px 14px;
		font-size: 13px;
		line-height: 1.35;
		color: $ink;
		background: $surface;
		border: 1px solid $line;
		border-radius: 18px;
		outline: none;
		resize: none;

		&:focus {
			background: var(--white);
			border-color: #cbd5e0;
		}

		&::placeholder {
			color: $muted;
		}
	}
</style>
